import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { QrScanner } from "../components/QrScanButton";
import { istDateTime } from "../components/AnnaTicketSlip";
import { AnnaStats } from "./AnnaIssue";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Camera, CameraOff, CheckCircle2, AlertTriangle, XCircle, CircleCheck, CalendarX } from "lucide-react";
import { toast } from "sonner";

const BANNER = {
  ok: { cls: "bg-green-700", Icon: CheckCircle2, title: "VERIFIED — ALLOW ENTRY" },
  used: { cls: "bg-orange-600", Icon: AlertTriangle, title: "ALREADY VERIFIED" },
  expired: { cls: "bg-red-700", Icon: CalendarX, title: "NOT VALID TODAY" },
  notfound: { cls: "bg-red-700", Icon: XCircle, title: "TICKET NOT FOUND" },
};

function beep(ok) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const tone = (f, at, len) => {
      const o = ctx.createOscillator(); const g = ctx.createGain();
      o.frequency.value = f; g.gain.value = 0.15; o.connect(g); g.connect(ctx.destination);
      o.start(ctx.currentTime + at); o.stop(ctx.currentTime + at + len);
    };
    if (ok) tone(1200, 0, 0.15); else { tone(300, 0, 0.25); tone(300, 0.35, 0.25); }
  } catch { /* no audio */ }
  try { navigator.vibrate?.(ok ? 120 : [250, 120, 250]); } catch { /* ignore */ }
}

export default function AnnaVerify() {
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const [scanKey, setScanKey] = useState(0);
  const [recent, setRecent] = useState([]);
  const [stats, setStats] = useState(null);
  const inputRef = useRef(null);
  const lastScan = useRef({ code: "", at: 0 });
  const isPhone = /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent);

  const loadStats = useCallback(() => { api.get("/anna/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  useEffect(() => { loadStats(); const t = setInterval(loadStats, 30000); return () => clearInterval(t); }, [loadStats]);
  const refocus = () => { if (!isPhone) setTimeout(() => inputRef.current?.focus(), 0); };
  useEffect(refocus, []); // eslint-disable-line

  const verify = async (value) => {
    const c = String(value ?? code).trim();
    if (!c || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/anna/verify", { code: c });
      setRes(r.data);
      beep(r.data.result === "ok");
      setRecent((x) => [{ ...r.data, at: new Date().toISOString(), code: c }, ...x].slice(0, 8));
      if (r.data.result === "ok") setStats((s) => s && { ...s, verified: (s.verified || 0) + 1 });
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
      setCode("");
      refocus();
    }
  };

  // Camera: one scan per QrScanner mount; remount for the next ticket. The ticket just scanned
  // may still be in front of the camera, so it is ignored until a different ticket is scanned
  // (type the number to check the same ticket again).
  const onScan = (text) => {
    const now = Date.now();
    if (text === lastScan.current.code) {
      setTimeout(() => setScanKey((k) => k + 1), 800);
      return;
    }
    lastScan.current = { code: text, at: now };
    verify(text).finally(() => setTimeout(() => setScanKey((k) => k + 1), 1200));
  };

  const t = res?.ticket;
  const banner = res && BANNER[res.result];

  return (
    <div className="max-w-3xl mx-auto" data-testid="anna-verify-page">
      <div className="text-center mb-4">
        <p className="text-xs uppercase tracking-[0.25em] text-gold-700 font-semibold">Annaprasadam Ticket Issue &amp; Verification System</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900">Ticket Verification</h1>
      </div>

      <Card className="card-gold-top">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <Input ref={inputRef} value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && verify()}
              placeholder="Scan QR or type ticket number (e.g. 150)" inputMode="text" autoComplete="off"
              className="h-12 text-lg font-mono" data-testid="anna-verify-input" />
            <div className="flex gap-2">
              <Button onClick={() => verify()} disabled={busy} className="h-12 flex-1 sm:flex-none px-5 bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="anna-verify-btn">
                <CircleCheck className="w-5 h-5 mr-1" /> Verify
              </Button>
              <Button onClick={() => setCamera((v) => !v)} variant="outline" className="h-12 flex-1 sm:flex-none px-5" data-testid="anna-camera-btn">
                {camera ? <><CameraOff className="w-5 h-5 mr-1" /> Close Camera</> : <><Camera className="w-5 h-5 mr-1" /> Scan QR</>}
              </Button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2 text-center">Desktop: use the QR scanner — it types the number and presses Enter. Mobile: tap <b>Scan QR</b>; the camera stays on for the next ticket.</p>

          {banner && (
            <div className={`mt-4 rounded-xl text-white p-4 flex items-center gap-3 shadow-lg ${banner.cls}`} data-testid="anna-result" data-result={res.result}>
              <banner.Icon className="w-12 h-12 shrink-0" />
              <div>
                <p className="font-heading text-xl sm:text-2xl font-bold tracking-wide">{banner.title}</p>
                <p className="text-sm opacity-95">
                  {res.result === "ok" && <>Ticket {t.ticket_no} · {t.category_name} · {t.ticket_type_name}</>}
                  {res.result === "used" && <>Ticket {t.ticket_no} was verified at {istDateTime(t.verified_at)}{t.verified_by ? ` by ${t.verified_by}` : ""}</>}
                  {res.result === "expired" && <>Ticket {t.ticket_no} was issued on {istDateTime(t.created_at)}</>}
                  {res.result === "notfound" && <>"{res.code}" — check the number or scan again</>}
                </p>
              </div>
            </div>
          )}

          {camera && (
            <div className="mt-4 rounded-xl overflow-hidden bg-black max-w-md mx-auto">
              <QrScanner key={scanKey} className="min-h-[300px]" onScan={onScan} onError={() => setCamera(false)} />
            </div>
          )}
        </CardContent>
      </Card>

      {t && (
        <Card className="mt-4">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-4">
            {t.photo
              ? <img src={t.photo} alt="devotee" className="w-full sm:w-48 h-56 sm:h-44 object-cover rounded-lg border-2 border-gold-400" data-testid="anna-verify-photo" />
              : <div className="w-full sm:w-48 h-44 rounded-lg border-2 border-dashed border-gold-400 flex items-center justify-center text-slate-400 text-sm">No photo</div>}
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm flex-1">
              {[["Ticket No", <span className="font-mono font-bold text-maroon-800">{t.ticket_no}</span>], ["Type", t.ticket_type_name], ["Category", t.category_name],
                ["Issued", istDateTime(t.created_at)], ["Issued By", t.issued_by], ["Counter", [t.counter_name, t.shift_name].filter(Boolean).join(" · ") || "-"],
                ["Verified", istDateTime(t.verified_at)], ["Verified By", t.verified_by || "-"],
                ["Verify Gate", [t.verified_counter_name, t.verified_shift_name].filter(Boolean).join(" · ") || "-"]].map(([k, v]) => (
                <div key={k}><p className="text-[11px] text-gold-700 font-bold uppercase">{k}</p><p>{v}</p></div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {recent.length > 1 && (
        <Card className="mt-4">
          <CardContent className="p-4">
            <p className="font-semibold text-maroon-900 mb-2">Recent scans</p>
            <ul className="divide-y text-sm">
              {recent.slice(1).map((r, i) => (
                <li key={i} className="py-1.5 flex justify-between gap-2">
                  <span className="font-mono">{r.ticket?.ticket_no || r.code}</span>
                  <span className={r.result === "ok" ? "text-green-700 font-semibold" : "text-red-700 font-semibold"}>{BANNER[r.result]?.title}</span>
                  <span className="text-slate-500">{new Date(r.at).toLocaleTimeString("en-IN")}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card className="mt-4">
        <CardContent className="p-4">
          <p className="font-heading font-bold text-maroon-900 text-center mb-3">Live Statistics (Today · all ticket types)</p>
          <AnnaStats stats={stats} />
        </CardContent>
      </Card>
    </div>
  );
}
